import { GameContext, InPlay, PeriodScoresResult, ScopeCompletedResolver } from '../../interfaces';
import { PeriodType } from '../../enums';
import { calculateTimeProgress } from './basketball.utils';

export const periodTextMap: Partial<Record<PeriodType, string>> = {
	[PeriodType.Game]: '',
	[PeriodType.Period1]: ' in the 1st period',
	[PeriodType.Period2]: ' in the 2nd period',
	[PeriodType.Period3]: ' in the 3rd period',
};

export const hockeyScopeResolver: ScopeCompletedResolver = (period, { isCompleted, inPlay }) => {
	if (isCompleted) return true;

	const currentPeriod = Number(inPlay?.period ?? 0);
	const clock = inPlay?.clock ?? null;

	if (!currentPeriod) return false;

	let endPeriod = 0;

	switch (period) {
		case PeriodType.Game:
			return isCompleted;
		case PeriodType.Period1:
			endPeriod = 1;
			break;
		case PeriodType.Period2:
			endPeriod = 2;
			break;
		case PeriodType.Period3:
			endPeriod = 3;
			break;
	}

	if (currentPeriod > endPeriod) return true;

	if (currentPeriod === endPeriod) {
		return clock === '00:00' || clock === null;
	}

	return false;
};

// NHL: 3 × 20-minute periods, 5-minute OT (regular season)
export const hockeyTimeProgressResolver = (inPlay: InPlay) =>
	calculateTimeProgress(inPlay, { periodMinutes: 20, regulationPeriods: 3, overtimeMinutes: 5 });

export const getPeriodScores = (gc: GameContext, period: PeriodType): PeriodScoresResult => {
	switch (period) {
		case PeriodType.Game:
			return {
				home: gc.homeScore,
				away: gc.awayScore,
				isCompleted: gc.isCompleted,
			};

		case PeriodType.Period1:
		case PeriodType.Period2:
		case PeriodType.Period3:
			return {
				home: gc.periodScores.home[period] ?? 0,
				away: gc.periodScores.away[period] ?? 0,
				isCompleted: gc.isScopeCompleted(period),
			};

		default:
			return { home: 0, away: 0, isCompleted: false };
	}
};

export const buildSpreadQuestion = (
	selection: string,
	points: number,
	period: PeriodType
): string => {
	const goals = Math.abs(points);
	const suffix = periodTextMap[period];

	if (points < 0) return `Will ${selection} win by more than ${goals} goals${suffix}?`;

	return `Will ${selection} avoid losing by more than ${goals} goals${suffix}?`;
};

export const buildTotalGoalsQuestion = (points: number, period: PeriodType): string => {
	const goals = Math.abs(points);
	const suffix = periodTextMap[period];

	return `Will the teams combine to score at least ${goals} goals${suffix}?`;
};

export const buildTeamTotalQuestion = (
	team: string,
	points: number,
	period: PeriodType
): string => {
	const goals = Math.abs(points);
	const suffix = periodTextMap[period];

	return `Will ${team} score at least ${goals} goals${suffix}?`;
};
